import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, checkNetworkConnectivity, handleSupabaseError } from '../lib/supabase';
import { logSecurityEvent, upsertCurrentSession } from '../services/securityService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [session, setSession] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)
  const [authReady, setAuthReady] = useState(false)
  const [isDemoAdmin, setIsDemoAdmin] = useState(false) // Track demo admin status
  const [networkError, setNetworkError] = useState(false);
  const [authErrorCallback, setAuthErrorCallback] = useState(null);

  // Check network connectivity on mount
  useEffect(() => {
    const checkNetwork = async () => {
      const isConnected = await checkNetworkConnectivity();
      setNetworkError(!isConnected);
      
      if (!isConnected) {
        console.warn('Network connectivity issue detected. Some features may not work.');
      }
    };
    
    checkNetwork();
    
    // Recheck network every 30 seconds
    const intervalId = setInterval(checkNetwork, 30000);
    
    return () => clearInterval(intervalId);
  }, []);

  // Isolated async operations - never called from auth callbacks
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        // First try to get existing profile
        let { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        
        // If profile doesn't exist, try to create it
        if (error && error?.code === 'PGRST116') {
          console.log('Profile not found, attempting to create profile for user:', userId)
          
          // Call the ensure_user_profile function to create profile if missing
          const { data: ensureResult, error: ensureError } = await supabase?.rpc('ensure_user_profile', { 
            user_uuid: userId 
          })
          
          if (ensureError) {
            console.error('Failed to ensure user profile:', ensureError)
          } else if (ensureResult) {
            // Try to fetch the profile again after creation
            const { data: newProfileData, error: newProfileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
            
            if (!newProfileError && newProfileData) {
              setUserProfile(newProfileData)
              console.log('Profile successfully created and loaded for user:', userId)
            }
          }
        } else if (!error && data) {
          setUserProfile(data)
        } else if (error && error?.code !== 'PGRST116') {
          console.error('Profile load error:', error)
        }
      } catch (error) {
        console.warn('Profile load failed:', error?.message)
      } finally {
        setProfileLoading(false)
      }
    },

    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Auth state handlers - PROTECTED from async modification
  const authStateHandlers = {
    // This handler MUST remain synchronous - Supabase requirement
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      setAuthReady(true) // Mark auth as ready after first state change
      
      // Clear demo admin flag when real auth changes
      setIsDemoAdmin(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
      } else {
        profileOperations?.clear()
      }
    }
  }

  // Enhanced initialization with demo admin session recovery
  useEffect(() => {
    let mounted = true;
    
    // Register global auth error callback
    setAuthErrorCallback(() => {
      if (mounted) {
        setUser(null);
        setSession(null);
        setUserProfile(null);
        profileOperations?.clear();
      }
    });
    
    const initializeAuth = async () => {
      try {
        setLoading(true);
        
        // Check network before attempting session check
        const isConnected = await checkNetworkConnectivity();
        if (!isConnected) {
          setNetworkError(true);
          setLoading(false);
          setAuthReady(true);
          return;
        }
        
        // Set a timeout for session check
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Session check timeout')), 10000)
        );
        
        const sessionPromise = supabase?.auth?.getSession();
        
        try {
          const { data: { session }, error } = await Promise.race([
            sessionPromise,
            timeoutPromise
          ]);

          if (error) {
            console.error('Session check failed:', error?.message);
            
            // Handle invalid refresh token error by clearing session
            if (error?.message?.includes('Invalid Refresh Token') || error?.message?.includes('Refresh Token Not Found')) {
              console.log('Invalid refresh token detected, clearing session and signing out...');
              try {
                await supabase?.auth?.signOut({ scope: 'local' });
              } catch (signOutError) {
                console.error('Error during sign out:', signOutError);
              }
            }
            
            if (mounted) {
              setUser(null);
              setSession(null);
            }
            return;
          }

          if (mounted) {
            setSession(session);
            setUser(session?.user ?? null);

            if (session?.user) {
              await fetchUserProfile(session?.user?.id);
            }
          }
        } catch (raceError) {
          // Handle refresh token errors that occur during session check
          if (raceError?.message?.includes('Invalid Refresh Token') || raceError?.message?.includes('Refresh Token Not Found')) {
            console.log('Invalid refresh token detected during session check, clearing session...');
            try {
              await supabase?.auth?.signOut({ scope: 'local' });
            } catch (signOutError) {
              console.error('Error during sign out:', signOutError);
            }
            if (mounted) {
              setUser(null);
              setSession(null);
            }
            return;
          }
          throw raceError;
        }
      } catch (error) {
        console.error('Auth initialization error:', error?.message);
        if (mounted) {
          setUser(null);
          setSession(null);
          if (error?.message?.includes('timeout')) {
            setNetworkError(true);
          }
        }
      } finally {
        if (mounted) {
          setLoading(false);
          setAuthReady(true); // Always mark auth as ready after initialization completes
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, []);

  // CRITICAL FIX: Subscribe to auth state changes
  useEffect(() => {
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      async (event, session) => {
        // Handle token refresh errors - Supabase fires this when refresh token is invalid
        if (event === 'TOKEN_REFRESHED' && !session) {
          console.warn('Token refresh failed, clearing session and signing out...');
          try {
            await supabase?.auth?.signOut({ scope: 'local' });
          } catch (error) {
            console.error('Error during sign out:', error);
          }
          setUser(null);
          setSession(null);
          profileOperations?.clear();
          return;
        }

        // Handle auth error events (invalid refresh token etc.)
        if (event === 'SIGNED_OUT' || (!session && (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED'))) {
          setUser(null);
          setSession(null);
          profileOperations?.clear();
          return;
        }
        
        // Handle signed out event
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setSession(null);
          profileOperations?.clear();
          return;
        }
        
        // Call the original handler
        authStateHandlers?.onChange(event, session);
      }
    );

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Enhanced auth methods with better error handling
  const fetchUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();
      
      if (error && error?.code === 'PGRST116') {
        console.log('Profile not found, attempting to create profile for user:', userId)
        
        // Call the ensure_user_profile function to create profile if missing
        const { data: ensureResult, error: ensureError } = await supabase?.rpc('ensure_user_profile', { 
          user_uuid: userId 
        })
        
        if (ensureError) {
          console.error('Failed to ensure user profile:', ensureError)
        } else if (ensureResult) {
          // Try to fetch the profile again after creation
          const { data: newProfileData, error: newProfileError } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
          
          if (!newProfileError && newProfileData) {
            setUserProfile(newProfileData)
            console.log('Profile successfully created and loaded for user:', userId)
          }
        }
      } else if (!error && data) {
        setUserProfile(data)
      } else if (error && error?.code !== 'PGRST116') {
        console.error('Profile load error:', error)
      }
    } catch (error) {
      console.warn('Profile load failed:', error?.message)
    } finally {
      setProfileLoading(false)
    }
  }

  const signUp = async (email, password, metadata = {}) => {
    try {
      const isConnected = await checkNetworkConnectivity();
      if (!isConnected) {
        throw new Error('Network connection error. Please check your internet connection.');
      }

      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: metadata,
          emailRedirectTo: 'https://fortifiedmind.org/dashboard',
        },
      });

      if (error) {
        const errorInfo = handleSupabaseError(error, 'signUp');
        throw new Error(errorInfo.error);
      }

      // Log signup event if user was created
      if (data?.user?.id) {
        logSecurityEvent(data?.user?.id, 'signup', 'success', { method: 'email' });
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign up error:', error);
      return { data: null, error };
    }
  };

  const signIn = async (email, password) => {
    try {
      const isConnected = await checkNetworkConnectivity();
      if (!isConnected) {
        throw new Error('Network connection error. Please check your internet connection.');
      }

      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password,
      });

      if (error) {
        const errorInfo = handleSupabaseError(error, 'signIn');
        
        if (errorInfo?.shouldSignOut) {
          await supabase?.auth?.signOut();
          setUser(null);
          setSession(null);
          profileOperations?.clear();
        }
        
        throw new Error(errorInfo.error);
      }

      // Log successful login and create session record
      if (data?.user?.id) {
        logSecurityEvent(data?.user?.id, 'login', 'success', { method: 'email' });
        upsertCurrentSession(data?.user?.id);
      }

      return { data, error: null };
    } catch (error) {
      console.error('Sign in error:', error);
      return { data: null, error };
    }
  }

  const signOut = async () => {
    try {
      if (isDemoAdmin) {
        setUser(null)
        setUserProfile(null)
        setIsDemoAdmin(false)
        profileOperations?.clear()
        localStorage.removeItem('demoAdminSession')
        return { error: null }
      }

      // Log logout before signing out (while we still have user id)
      const currentUserId = user?.id;
      if (currentUserId) {
        await logSecurityEvent(currentUserId, 'logout', 'success');
      }
      
      const { error } = await supabase?.auth?.signOut()
      if (!error) {
        setUser(null)
        profileOperations?.clear()
        localStorage.removeItem('demoAdminSession')
      }
      return { error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error && data) {
        setUserProfile(data)
      }
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const signInWithGoogle = async () => {
    try {
      const { data, error } = await supabase?.auth?.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: 'https://fortifiedmind.org',
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          },
        },
      });
      if (error) {
        return { data: null, error };
      }
      return { data, error: null };
    } catch (error) {
      console.error('Google sign-in error:', error);
      return { data: null, error };
    }
  };

  // ENHANCED: Update global window context whenever auth state changes
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.authContext = {
        user,
        userProfile,
        isDemoAdmin,
        isAdmin: userProfile?.role === 'admin'
      };
    }
    
    return () => {
      if (typeof window !== 'undefined' && !user) {
        delete window.authContext;
      }
    };
  }, [user, userProfile, isDemoAdmin]);

  const value = {
    user,
    userProfile,
    session,
    profile,
    loading,
    networkError,
    authReady,
    isDemoAdmin,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    updateProfile,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
export { AuthContext };